import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { currentAppLanguage } from "../../utils/appLanguages";
import { localizedExerciseName, localizedField as getLocalized } from "../../utils/localizedCatalogFields";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HomeIcon from "@mui/icons-material/Home";
import RefreshIcon from "@mui/icons-material/Refresh";
import BoltIcon from "@mui/icons-material/Bolt";
import ShieldIcon from "@mui/icons-material/Shield";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import TimerIcon from "@mui/icons-material/Timer";
import PauseIcon from "@mui/icons-material/Pause";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
} from "../../utils/coachExerciseNavigation";
import { isMultiballExercise, isTeSequences } from "../../utils/teExecutionType";
import {
  calculateMHR,
  getZoneThresholds,
  ZONE_DEFS,
  ZONE_CHART_COLORS,
} from "../../features/trainingSummary/zoneUtils";
import {
  deriveViewerSlotFromSequence,
  formatPlayerStats,
  normalizeExerciseRole,
  pickViewerSequence,
  pickSequenceForViewerTable,
  playerClassement,
  playerDisplayName,
  playerRankingPoints,
  resolvePlayerDisplayFields,
  resolveViewerTableContext,
} from "../../utils/liveTrainingPlayerContext";
import { useBrand } from "../../context/BrandContext";
import { LiveTrainingClubLogo } from "./LiveTrainingClubLogo";
import { LiveTrainingHeaderBrand } from "./LiveTrainingHeader";
import { LiveHrTrendPanel } from "../../components/LiveTraining/LiveHrTrendPanel";
import { resolveLiveSequence } from "../../utils/liveTrainingSequenceResolve";
import { PanelDisplayModeToggle } from "../../components/LiveTraining/PanelDisplayModeToggle";
import { schemaKeyFromExercise, schemaLabel } from "../../components/funtionsBD/tacticalPatternService";
import { Link } from "react-router-dom";
import { playerHasCollectingHr } from "../../utils/liveHrHistoryBuffer";
import { isOffTableExercise } from "../../utils/teExecutionType";
import { coachToolsFromExercise } from "../../utils/teCoachTools";
import { LiveTrainingOffTableLandscape } from "./LiveTrainingOffTableLandscape";
import { StationRecordingRoster } from "./StationRecordingRoster";

function PlayerHeaderActions({ onDashboard, onRefresh, refreshing, t }) {
  return (
    <div className="lt-header__actions">
      {onRefresh ? (
        <button
          type="button"
          className={`lt-header__icon-btn${
            refreshing ? " lt-header__icon-btn--spinning" : ""
          }`}
          onClick={onRefresh}
          disabled={refreshing}
          aria-label={t("liveTrainingRefresh")}
          title={t("liveTrainingRefresh")}
        >
          <RefreshIcon fontSize="small" />
        </button>
      ) : null}
      {onDashboard ? (
        <button
          type="button"
          className="lt-header__home-btn"
          onClick={onDashboard}
          aria-label={t("home") || "Home"}
          title={t("home") || "Home"}
        >
          <HomeIcon fontSize="small" />
          <span className="lt-header__home-label">{t("home") || "Home"}</span>
        </button>
      ) : null}
    </div>
  );
}

/**
 * Same club logo + session title as the coach header, optional table/detail
 * line, theme + front-camera chip, refresh + home. Player names stay out of
 * the title.
 */
function PlayerChromeHeader({
  user,
  session,
  detail = "",
  isLandscape = false,
  onDashboard,
  onRefresh,
  refreshing,
  isFrontCamera = false,
  t,
}) {
  const { themeMode, setThemeMode } = useBrand();
  const isLight = themeMode === "light";

  return (
    <header className="lt-pv__chrome">
      <LiveTrainingHeaderBrand
        session={session}
        user={user}
        isLandscape={isLandscape}
        subtitle={detail}
      />

      <div className="lt-pv__chrome-right">
        <button
          type="button"
          className="lt-header__icon-btn"
          onClick={() => setThemeMode(isLight ? "dark" : "light")}
          aria-label={t("themeMode", "Theme Mode")}
          title={
            isLight
              ? t("darkMode", "Dark")
              : t("lightMode", "Light")
          }
          aria-pressed={isLight}
        >
          {isLight ? (
            <DarkModeIcon fontSize="small" />
          ) : (
            <LightModeIcon fontSize="small" />
          )}
        </button>

        {isFrontCamera ? (
          <span
            className="lt-pv__screen-awake-chip"
            title={t(
              "liveTrainingFrontCamAwake",
              "Screen kept on while using front camera"
            )}
          >
            <VisibilityIcon fontSize="inherit" />
            <span className="lt-pv__chip-label">
              {t("liveTrainingFrontCamAwake", "Screen Awake")}
            </span>
          </span>
        ) : null}

        <PlayerHeaderActions
          onDashboard={onDashboard}
          onRefresh={onRefresh}
          refreshing={refreshing}
          t={t}
        />
      </div>
    </header>
  );
}

function isSparringExerciseRole(role) {
  const key = String(role || "").toLowerCase();
  return key.includes("spar");
}

/** Portrait HR biometrics power card or expandable 60s trend chart (viewer only). */
function PortraitViewerHrBlock({
  panelDisplayMode,
  showHrToggle,
  showHrUi,
  onTogglePanelMode,
  viewerHrSamples,
  mhr,
  viewerHr,
  viewerMaxHr,
  viewerIsSparring,
  liveZone,
  zoneFill,
  zoneFg,
  playerName,
  classement,
  rankingPoints,
  roleText,
  t,
}) {
  if (!showHrUi) return null;

  if (panelDisplayMode === "hrTrend" && showHrToggle) {
    return (
      <div className="lt-pv__hr-trend-block">
        <LiveHrTrendPanel
          player={{
            name: playerName,
            classement,
            rankingPoints,
          }}
          role={roleText}
          samples={viewerHrSamples}
          mhr={mhr}
          layout="panel"
          chartHeight={160}
          liveBpm={viewerHr}
          toggle={
            <PanelDisplayModeToggle
              mode={panelDisplayMode}
              onToggle={onTogglePanelMode}
              className="lt-pv__panel-mode-toggle"
            />
          }
        />
      </div>
    );
  }

  const currentZone = liveZone != null ? liveZone : 0;
  const isBeating = viewerHr != null && Number(viewerHr) > 0;
  const zoneTitle =
    ZONE_DEFS.find((d) => d.zone === currentZone)?.title ||
    (currentZone === 0
      ? t("liveTrainingZoneWarmup", "Warmup / Rest")
      : `ZONE ${currentZone}`);

  return (
    <div
      className={`lt-pv__hr-power-card${
        viewerIsSparring ? " lt-pv__hr-power-card--neutral" : ""
      }`}
      style={
        viewerIsSparring
          ? undefined
          : {
              "--lt-zone": zoneFill || "rgba(255,255,255,0.06)",
              "--lt-zone-fg": zoneFg || "#f8fafc",
            }
      }
    >
      <div className="lt-pv__hr-top-row">
        <div className="lt-pv__hr-metric">
          <FavoriteIcon
            className={`lt-pv__hr-heart-icon${
              isBeating ? " lt-pv__hr-heart-icon--beating" : ""
            }`}
          />
          <span className="lt-pv__hr-live-number">
            {viewerHr != null ? viewerHr : "--"}
          </span>
          <span className="lt-pv__hr-live-unit">BPM</span>
        </div>

        <div className="lt-pv__hr-right-meta">
          <span className="lt-pv__hr-peak-pill">
            {t("liveTrainingMaxBpm", {
              n: viewerMaxHr != null ? viewerMaxHr : "—",
            })}
          </span>
          {showHrToggle ? (
            <PanelDisplayModeToggle
              mode={panelDisplayMode}
              onToggle={onTogglePanelMode}
              className="lt-pv__panel-mode-toggle lt-pv__panel-mode-toggle--inline"
            />
          ) : null}
        </div>
      </div>

      {/* 5-Zone Spectrum Gauge */}
      {!viewerIsSparring ? (
        <div className="lt-pv__hr-zones-bar">
          {[1, 2, 3, 4, 5].map((z) => {
            const isCurrent = currentZone === z;
            const isPast = currentZone > z;
            return (
              <div
                key={z}
                className={`lt-pv__hr-zone-segment${
                  isCurrent
                    ? " lt-pv__hr-zone-segment--active"
                    : isPast
                    ? " lt-pv__hr-zone-segment--past"
                    : ""
                }`}
                style={{ "--segment-color": ZONE_CHART_COLORS[z] }}
                title={`Zone ${z}`}
              >
                <span className="lt-pv__hr-zone-pip">Z{z}</span>
              </div>
            );
          })}
        </div>
      ) : null}

      {!viewerIsSparring && currentZone > 0 ? (
        <div className="lt-pv__hr-zone-legend">
          <span className="lt-pv__hr-zone-tag">⚡ {zoneTitle}</span>
        </div>
      ) : null}
    </div>
  );
}

/** Club logo + table number block for landscape center column. */
function TableCenterBrand({ user, tableNumber, t }) {
  return (
    <div className="lt-pv__table-center-brand">
      <LiveTrainingClubLogo
        user={user}
        size="lg"
        className="lt-pv__table-center-logo"
      />
      <div className="lt-pv__table-center-table">
        <span className="lt-pv__table-center-label">{t("table")}</span>
        <span className="lt-pv__table-center-number">
          {tableNumber != null ? `#${tableNumber}` : "—"}
        </span>
      </div>
    </div>
  );
}

/** One player column in landscape table layout (waiting or active). */
function LandscapePlayerSide({
  side,
  player,
  role,
  roleKey,
  isViewer,
  viewerHr,
  exerciseActive,
  mhr,
  t,
  displayMode = "default",
  onToggleDisplayMode,
  hrSamples = [],
  showHrToggle = false,
  showHrUi = false,
}) {
  const stats = formatPlayerStats(player?.classement, player?.rankingPoints, t);
  const sideClass = side === "A" ? "a" : "b";
  const bpm = isViewer && viewerHr != null ? viewerHr : null;
  const isSparring = isSparringExerciseRole(roleKey);
  const showZonePill =
    showHrUi && exerciseActive && bpm != null && bpm > 0 && !isSparring;
  const liveZone = showZonePill ? zoneForBpm(bpm, mhr) : null;
  const zoneFill = zoneColor(liveZone);
  const zoneFg = zoneTextColor(liveZone);
  const isHrTrend =
    showHrUi && displayMode === "hrTrend" && isViewer && showHrToggle;

  if (isHrTrend) {
    return (
      <div
        className={`lt-pv__table-side lt-pv__table-side--${sideClass} lt-pv__table-side--viewer lt-pv__side--hr-trend`}
      >
        <LiveHrTrendPanel
          player={player}
          role={role}
          samples={hrSamples}
          mhr={mhr}
          layout="panel"
          liveBpm={bpm}
          toggle={
            <PanelDisplayModeToggle
              mode={displayMode}
              onToggle={onToggleDisplayMode}
              className="lt-pv__panel-mode-toggle"
            />
          }
        />
      </div>
    );
  }

  return (
    <div
      className={`lt-pv__table-side lt-pv__table-side--${sideClass}${
        isViewer ? " lt-pv__table-side--viewer" : ""
      }`}
    >
      {showHrUi && showHrToggle ? (
        <div className="lt-pv__side-toggle-row">
          <PanelDisplayModeToggle
            mode={displayMode}
            onToggle={onToggleDisplayMode}
            className="lt-pv__panel-mode-toggle"
          />
        </div>
      ) : null}
      <span className="lt-pv__side-letter" aria-hidden="true">
        {side}
      </span>
      <div className="lt-pv__side-body">
        <div className="lt-pv__side-head">
          <p className="lt-pv__side-name">{player?.name || "—"}</p>
        </div>
        {role ? (
          <span
            className={`lt-pv__role lt-pv__role--side lt-pv__role--${
              isSparring ? "sparring" : "active"
            }`}
          >
            {role}
          </span>
        ) : null}
        {stats ? <span className="lt-pv__side-stats">{stats}</span> : null}
      </div>
      {showHrUi && isViewer ? (
        <div
          className={`lt-pv__hr-chip${showZonePill ? " lt-pv__hr-chip--zone" : ""}`}
          style={
            showZonePill
              ? {
                  "--lt-zone": zoneFill,
                  "--lt-zone-fg": zoneFg,
                }
              : undefined
          }
        >
          <FavoriteIcon className="lt-pv__hr-icon" />
          <span>{bpm != null ? bpm : "—"}</span>
        </div>
      ) : null}
    </div>
  );
}

function resolveLandscapeSidePlayer(
  side,
  tablePlayers,
  seq,
  roster,
  viewerPlayerId,
  reservedPlayerIds = []
) {
  const reserved = new Set(reservedPlayerIds.filter(Boolean).map(String));
  const isReserved = (p) =>
    p?.playerId != null && reserved.has(String(p.playerId));

  const slotId = side === "A" ? seq?.player_a_id : seq?.player_b_id;
  const slotIndex = side === "A" ? 0 : 1;

  const fromTable =
    tablePlayers.find((p) => p.side === side && !isReserved(p)) ||
    (slotId
      ? tablePlayers.find(
          (p) =>
            p.playerId != null &&
            String(p.playerId) === String(slotId) &&
            !isReserved(p)
        )
      : null) ||
    (() => {
      const candidate = tablePlayers[slotIndex];
      return candidate && !isReserved(candidate) ? candidate : null;
    })();

  if (fromTable?.name || fromTable?.playerId) {
    const refreshed = resolvePlayerDisplayFields(
      fromTable,
      roster,
      fromTable.playerId
    );
    const embeddedName =
      typeof fromTable.name === "string" ? fromTable.name.trim() : "";
    return {
      ...fromTable,
      ...refreshed,
      name: refreshed.name || embeddedName || "—",
      side: fromTable.side || side,
      role:
        fromTable.role ||
        (side === "A" ? seq?.player_a_role : seq?.player_b_role) ||
        null,
      isViewer:
        fromTable.isViewer ||
        (viewerPlayerId != null &&
          fromTable.playerId != null &&
          String(fromTable.playerId) === String(viewerPlayerId)),
    };
  }

  if (!slotId || isReserved({ playerId: slotId })) return null;

  const onThisTable = tablePlayers.some(
    (p) => p.playerId != null && String(p.playerId) === String(slotId)
  );
  if (!onThisTable) return null;

  const rosterEntry = Array.isArray(roster)
    ? roster.find((r) => String(r.player_id) === String(slotId))
    : null;

  return {
    playerId: slotId,
    name: playerDisplayName(rosterEntry) || null,
    classement: playerClassement(null, rosterEntry),
    rankingPoints: playerRankingPoints(null, rosterEntry),
    side,
    role: side === "A" ? seq?.player_a_role : seq?.player_b_role,
    isViewer: viewerPlayerId != null && String(slotId) === String(viewerPlayerId),
  };
}

function getInitials(name) {
  if (!name || name === "—") return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

function RoleCrest({ role, roleKey, t }) {
  const isSparring = isSparringExerciseRole(roleKey || role);
  return (
    <span
      className={`lt-pv__role-crest${
        isSparring
          ? " lt-pv__role-crest--sparring"
          : " lt-pv__role-crest--performance"
      }`}
    >
      {isSparring ? (
        <ShieldIcon fontSize="inherit" className="lt-pv__role-crest-icon" />
      ) : (
        <BoltIcon fontSize="inherit" className="lt-pv__role-crest-icon" />
      )}
      <span>
        {role ||
          (isSparring
            ? t("sparring", "Sparring")
            : t("performance_role", "Performance"))}
      </span>
    </span>
  );
}

/** Gamified portrait table summary — stadium table, duo matchup cockpit, and tactical mission card. */
function PortraitTablePanel({
  tableNumber,
  resolvedSide,
  roleText,
  resolvedRole,
  viewerClassement,
  viewerRankingPoints,
  tablePlayers = [],
  playerName,
  exercise,
  titleText,
  typeText,
  subTypeText,
  schemaBadge,
  personalizedCue,
  tablePartner,
  isWaiting,
  t,
}) {
  const otherPlayers = (tablePlayers || []).filter(
    (p) =>
      !p?.isViewer &&
      (!tablePartner?.playerId || String(p.playerId) !== String(tablePartner.playerId))
  );

  return (
    <div
      className="lt-pv__table-panel lt-pv__table-panel--gamified"
      data-tour-id="live-player-table"
    >
      {/* Stadium Table & Status Header */}
      <div className="lt-pv__table-stadium-head">
        <div className="lt-pv__stadium-badge">
          <span className="lt-pv__stadium-label">{t("table", "Table")}</span>
          <span className="lt-pv__stadium-num">
            {tableNumber != null ? `#${tableNumber}` : "—"}
          </span>
        </div>
        <div className="lt-pv__stadium-status">
          {isWaiting ? (
            <span className="lt-pv__stadium-pill lt-pv__stadium-pill--standby">
              <span className="lt-pv__stadium-dot" />
              {t("liveTrainingReady", "READY // STANDBY")}
            </span>
          ) : (
            <span className="lt-pv__stadium-pill lt-pv__stadium-pill--live">
              <span className="lt-pv__stadium-dot lt-pv__stadium-dot--pulse" />
              {t("liveTrainingStationLive", "LIVE DRILL")}
            </span>
          )}
        </div>
      </div>

      {/* Duel / Matchup Arena: You vs Partner */}
      <div className="lt-pv__matchup-arena">
        {/* You Card */}
        <div className="lt-pv__matchup-card lt-pv__matchup-card--you">
          <div className="lt-pv__matchup-card-top">
            <div className="lt-pv__avatar lt-pv__avatar--you">
              {getInitials(playerName)}
            </div>
            <div className="lt-pv__matchup-meta">
              <span className="lt-pv__player-title">
                {t("liveTrainingYou", "YOU")}
                {playerName && playerName !== "—" ? ` · ${playerName}` : ""}
              </span>
              <div className="lt-pv__player-sub">
                {resolvedSide ? (
                  <span className="lt-pv__side-tag">{`SIDE ${resolvedSide}`}</span>
                ) : null}
                {viewerClassement ? (
                  <span className="lt-pv__stat-tag">{`CLS ${viewerClassement}`}</span>
                ) : null}
                {viewerRankingPoints ? (
                  <span className="lt-pv__stat-tag lt-pv__stat-tag--pts">{`⚡ ${viewerRankingPoints} PTS`}</span>
                ) : null}
              </div>
            </div>
          </div>
          <RoleCrest role={roleText} roleKey={resolvedRole} t={t} />
        </div>

        {/* VS / DUO connector */}
        {tablePartner?.name ? (
          <div className="lt-pv__matchup-vs-bridge">
            <span className="lt-pv__matchup-vs-badge">VS</span>
          </div>
        ) : null}

        {/* Partner Card */}
        {tablePartner?.name ? (
          <div className="lt-pv__matchup-card lt-pv__matchup-card--partner">
            <div className="lt-pv__matchup-card-top">
              <div className="lt-pv__avatar lt-pv__avatar--partner">
                {getInitials(tablePartner.name)}
              </div>
              <div className="lt-pv__matchup-meta">
                <span className="lt-pv__player-title">{tablePartner.name}</span>
                <div className="lt-pv__player-sub">
                  {tablePartner.side ? (
                    <span className="lt-pv__side-tag">{`SIDE ${tablePartner.side}`}</span>
                  ) : null}
                  {tablePartner.classement ? (
                    <span className="lt-pv__stat-tag">{`CLS ${tablePartner.classement}`}</span>
                  ) : null}
                </div>
              </div>
            </div>
            <RoleCrest
              role={normalizeExerciseRole(tablePartner.role, t, {
                multiball: isMultiballExercise(exercise),
              })}
              roleKey={tablePartner.role}
              t={t}
            />
          </div>
        ) : null}
      </div>

      {/* Extra Table Mates (if more than 2) */}
      {otherPlayers.length > 0 ? (
        <div className="lt-pv__roster-strip">
          <span className="lt-pv__roster-strip-label">
            {t("liveTrainingTablePlayers", "Table Players")}:
          </span>
          <div className="lt-pv__roster-strip-chips">
            {otherPlayers.map((p, idx) => (
              <span key={p.playerId || idx} className="lt-pv__roster-pill">
                {p.name} {p.side ? `(${p.side})` : ""}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* Tactical Mission Objective (shown when exercise info is available) */}
      {exercise && titleText ? (
        <div className="lt-pv__mission-card">
          <div className="lt-pv__mission-header">
            <SportsTennisIcon className="lt-pv__mission-tennis-icon" />
            <span className="lt-pv__mission-lead">
              {t("liveTrainingMissionObjective", "MISSION OBJECTIVE")}
            </span>
            {schemaBadge ? (
              <span className="lt-pv__schema-pill">⚡ {schemaBadge}</span>
            ) : null}
          </div>
          <p className="lt-pv__mission-title">{titleText}</p>
          {typeText !== "—" || subTypeText !== "—" ? (
            <div className="lt-pv__mission-meta">
              {typeText !== "—" ? (
                <span className="lt-pv__mission-type">{typeText}</span>
              ) : null}
              {subTypeText !== "—" ? (
                <span className="lt-pv__mission-subtype">
                  {typeText !== "—" ? " · " : ""}
                  {subTypeText}
                </span>
              ) : null}
            </div>
          ) : null}
          {personalizedCue ? (
            <div className="lt-pv__coach-cue-box">
              <span className="lt-pv__coach-cue-label">
                {t("liveTrainingCoachFocus", "COACH FOCUS")}:
              </span>
              <p className="lt-pv__coach-cue-text">{personalizedCue}</p>
            </div>
          ) : null}
          {tablePartner?.name ? (
            <Link
              className="lt-pv__prep-link"
              to={`/tactical/prep?name=${encodeURIComponent(
                tablePartner.name
              )}${
                tablePartner.playerId
                  ? `&playerId=${encodeURIComponent(tablePartner.playerId)}`
                  : ""
              }`}
            >
              <span>{t("schemaPrepCta", "Prepare opponent tactics")}</span>
              <ArrowForwardIosIcon className="lt-pv__prep-arrow-icon" />
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ─── helpers ─────────────────────────────────────────────────────────────── */

function formatTimer(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "--:--";
  const s = Math.max(0, Math.floor(Number(seconds)));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

/** Age/gender for MHR zone colours — mirrors TrainingSummaryPage logic. */
function resolvePhysiology(user) {
  let profiling = {};
  try {
    const raw =
      localStorage.getItem("profilingData") ||
      localStorage.getItem("playerFormData");
    if (raw) profiling = JSON.parse(raw) || {};
  } catch {
    profiling = {};
  }
  const genderRaw = profiling.gender ?? user?.gender ?? user?.sex ?? "male";
  const gender = String(genderRaw).toLowerCase() === "female" ? "female" : "male";
  let age = Number(user?.age);
  if (!Number.isFinite(age) || age <= 0) {
    const dob = profiling.dateOfBirth || user?.dateOfBirth;
    if (dob) {
      const birth = new Date(dob);
      if (!Number.isNaN(birth.getTime())) {
        const today = new Date();
        age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
      }
    }
  }
  if (!Number.isFinite(age) || age <= 0) age = 30;
  return { age, gender };
}

function zoneForBpm(bpm, mhr) {
  if (!bpm || bpm <= 0) return null;
  const thresholds = getZoneThresholds(mhr);
  for (let z = 5; z >= 0; z -= 1) {
    if (bpm >= thresholds[z][0]) return z;
  }
  return 0;
}

function zoneColor(zone) {
  if (zone == null) return null;
  return ZONE_DEFS.find((d) => d.zone === zone)?.color || null;
}

function zoneTextColor(zone) {
  if (zone == null) return "#f8fafc";
  return zone === 0 || zone === 1 || zone === 4 ? "#1f242e" : "#ffffff";
}

/* ─── component ───────────────────────────────────────────────────────────── */

export function LiveTrainingPlayerView({
  exercise,
  exerciseStateId,
  myTablePlayer,
  user,
  session = null,
  viewerHr,
  viewerMaxHr,
  viewerRole,
  viewerSide,
  viewerPlayerId,
  viewerTableNumber,
  viewerTableContext,
  roster,
  awaitingManualSequenceStart,
  awaitingAutoSequenceStart = false,
  sequenceTransitionPauseRemaining = null,
  currentSequenceIndex = null,
  isLandscape,
  isKot,
  onDashboard,
  onRefresh,
  refreshing = false,
  getHrSamples,
  tsId = null,
  tsgId = null,
  socket = null,
  isOffTable: isOffTableProp = null,
  coachTools: coachToolsProp = null,
  cameraFacingMode = "environment",
  isFrontCamera = false,
  tablePhoneOn = false,
  stationPhoneOn = false,
  stationLabel = null,
  stationTables = [],
  instructionRecording = false,
}) {
  const { t, i18n } = useTranslation();
  const lang = currentAppLanguage(i18n);
  const [panelDisplayMode, setPanelDisplayMode] = useState("default");

  const isStarted = exerciseStateId === TE_STATE_STARTED;
  const isPaused = exerciseStateId === TE_STATE_PAUSED;
  const isActiveState = isStarted || isPaused;

  const seqTimerRaw = exercise?.sequenceTimer;
  const exTimerRaw = exercise?.timer;
  const seqHasTime = Number(seqTimerRaw) > 0;

  const isWaiting =
    !isActiveState ||
    awaitingManualSequenceStart ||
    (isPaused && !seqHasTime);

  /* smooth local countdown anchored to the latest socket value */
  const [now, setNow] = useState(() => Date.now());
  const anchorRef = useRef({ seq: seqTimerRaw, ex: exTimerRaw, at: Date.now() });
  useEffect(() => {
    anchorRef.current = { seq: seqTimerRaw, ex: exTimerRaw, at: Date.now() };
    setNow(Date.now());
  }, [seqTimerRaw, exTimerRaw, exercise?.te_id, isPaused]);
  useEffect(() => {
    if (isWaiting || isPaused) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isWaiting, isPaused]);

  const elapsed = isPaused ? 0 : Math.floor((now - anchorRef.current.at) / 1000);
  const seqDisplay =
    seqTimerRaw != null ? Math.max(0, anchorRef.current.seq - elapsed) : null;
  const exDisplay =
    exTimerRaw != null ? Math.max(0, anchorRef.current.ex - elapsed) : null;

  const { number: seqNumber, obj: currentSeq } = resolveLiveSequence(
    exercise,
    currentSequenceIndex
  );

  /* text values */

  const exName = exercise?.personalized_pending
    ? t(
        "personalizedExercisePending",
        "Your coach still needs to validate your personalized exercise"
      )
    : localizedExerciseName(exercise, lang) || t("exercise");
  const personalizedCue =
    exercise?.personalized_slot && !exercise?.personalized_pending
      ? getLocalized(exercise, "ex_objective", lang) ||
        getLocalized(exercise, "ex_instruction", lang) ||
        t(
          "personalizedExerciseCue",
          "Your validated exercise for the primary objective"
        )
      : "";
  const order = exercise?.te_order;
  const titleText = order != null ? `${order}. ${exName}` : exName;
  const schemaKey = schemaKeyFromExercise(exercise);
  const schemaBadge = schemaKey ? schemaLabel(schemaKey, lang) : null;

  const typeText =
    getLocalized(exercise?.type_ex, "type_name", lang) ||
    getLocalized(exercise, "type_name", lang) ||
    "—";
  const subTypeText =
    getLocalized(currentSeq?.sub_type_ex, "sub_type_name", lang) ||
    getLocalized(currentSeq, "sub_type_name", lang) ||
    getLocalized(exercise?.sub_type_ex, "sub_type_name", lang) ||
    "—";

  const fallbackTableCtx = useMemo(
    () =>
      resolveViewerTableContext(
        null,
        viewerPlayerId,
        user?.user_id,
        exercise,
        roster
      ),
    [viewerPlayerId, user?.user_id, exercise, roster]
  );

  const tableCtx = useMemo(() => {
    if (viewerTableContext?.players?.length) return viewerTableContext;
    if (viewerTableContext?.tableNumber != null) return viewerTableContext;
    return fallbackTableCtx;
  }, [viewerTableContext, fallbackTableCtx]);

  const viewerFromTablePlayers = (tableCtx.players || []).find(
    (p) =>
      p?.isViewer ||
      (viewerPlayerId != null &&
        p?.playerId != null &&
        String(p.playerId) === String(viewerPlayerId))
  );
  const playerName =
    tableCtx.viewer?.name ||
    viewerFromTablePlayers?.name ||
    playerDisplayName(myTablePlayer) ||
    playerDisplayName(user) ||
    (typeof user?.name === "string" ? user.name.trim() : "") ||
    "";

  const viewerTableNum =
    tableCtx.tableNumber ?? viewerTableNumber ?? null;

  const tableSeq = useMemo(
    () => pickSequenceForViewerTable(exercise, viewerTableNum),
    [exercise, viewerTableNum]
  );

  const slotFromSeq = deriveViewerSlotFromSequence(
    exercise,
    viewerPlayerId,
    viewerTableNum
  );
  const resolvedSide = viewerSide || slotFromSeq?.side || tableCtx.viewer?.side || null;
  const resolvedRole = viewerRole || slotFromSeq?.role || tableCtx.viewer?.role || null;

  const firstSeq = tableSeq || pickViewerSequence(exercise);
  const activeSeq = currentSeq || tableSeq || firstSeq;
  const tableNumber =
    viewerTableNum ??
    activeSeq?.table_number ??
    currentSeq?.table_number ??
    firstSeq?.table_number ??
    exercise?.table_number ??
    null;

  const roleOpts = { multiball: isMultiballExercise(exercise) };
  const roleText = normalizeExerciseRole(resolvedRole, t, roleOpts);
  const viewerClassement =
    tableCtx.viewer?.classement ?? myTablePlayer?.classement ?? null;
  const tablePlayers = tableCtx.players?.length
    ? tableCtx.players
    : fallbackTableCtx.players || [];

  const landscapePlayerA = useMemo(
    () =>
      resolveLandscapeSidePlayer(
        "A",
        tablePlayers,
        tableSeq || activeSeq,
        roster,
        viewerPlayerId
      ),
    [tablePlayers, tableSeq, activeSeq, roster, viewerPlayerId]
  );
  const landscapePlayerB = useMemo(
    () =>
      resolveLandscapeSidePlayer(
        "B",
        tablePlayers,
        tableSeq || activeSeq,
        roster,
        viewerPlayerId,
        landscapePlayerA?.playerId ? [landscapePlayerA.playerId] : []
      ),
    [tablePlayers, tableSeq, activeSeq, roster, viewerPlayerId, landscapePlayerA]
  );

  const tablePartner =
    landscapePlayerA && String(landscapePlayerA.playerId) !== String(viewerPlayerId)
      ? landscapePlayerA
      : landscapePlayerB && String(landscapePlayerB.playerId) !== String(viewerPlayerId)
        ? landscapePlayerB
        : null;

  const viewerClassementValue = viewerClassement;
  const viewerRankingValue =
    tableCtx.viewer?.rankingPoints ??
    myTablePlayer?.ranking_points ??
    playerRankingPoints(myTablePlayer) ??
    playerRankingPoints(user);

  const portraitTablePanel = (
    <PortraitTablePanel
      tableNumber={tableNumber}
      resolvedSide={resolvedSide}
      roleText={roleText}
      resolvedRole={resolvedRole}
      viewerClassement={viewerClassementValue}
      viewerRankingPoints={viewerRankingValue}
      tablePlayers={tablePlayers}
      playerName={playerName}
      exercise={exercise}
      titleText={titleText}
      typeText={typeText}
      subTypeText={subTypeText}
      schemaBadge={schemaBadge}
      personalizedCue={personalizedCue}
      tablePartner={tablePartner}
      isWaiting={isWaiting}
      t={t}
    />
  );

  /* HR zone */
  const { age, gender } = resolvePhysiology(user);
  const mhr = calculateMHR({ age, gender });
  const liveZone = zoneForBpm(viewerHr, mhr);
  const zoneFill = zoneColor(liveZone);
  const zoneFg = zoneTextColor(liveZone);

  /* whether to show sequence timer row */
  const showSeqTimer = isTeSequences(exercise) && !isKot;

  useEffect(() => {
    setPanelDisplayMode("default");
  }, [exercise?.te_id]);

  const viewerHrSamples = useMemo(() => {
    if (!getHrSamples) return [];
    return getHrSamples(
      viewerPlayerId,
      user?.user_id ?? myTablePlayer?.user_id
    );
  }, [getHrSamples, viewerPlayerId, user?.user_id, myTablePlayer?.user_id]);

  const viewerIsSparring = isSparringExerciseRole(resolvedRole);
  const showHrUi =
    playerHasCollectingHr(viewerHrSamples) ||
    (viewerHr != null && Number(viewerHr) > 0);
  const showHrToggle = showHrUi && Boolean(getHrSamples);

  const handleTogglePanelMode = () => {
    setPanelDisplayMode((prev) =>
      prev === "hrTrend" ? "default" : "hrTrend"
    );
  };

  const landscapeSideProps = {
    displayMode: panelDisplayMode,
    onToggleDisplayMode: handleTogglePanelMode,
    hrSamples: viewerHrSamples,
    showHrToggle,
    showHrUi,
    exerciseActive: showHrUi,
    mhr,
    t,
  };

  const chromeDetail = [
    stationPhoneOn
      ? stationLabel || t("liveTrainingStationGroupVideo", "Group video")
      : tableNumber != null
        ? t("table_with_number", { number: tableNumber })
        : null,
    instructionRecording
      ? t("liveTrainingInstructionRec", "REC · Instructions")
      : null,
    isLandscape
      ? [titleText, schemaBadge, personalizedCue].filter(Boolean).join(" · ")
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const stationRoster = stationPhoneOn ? (
    <div className="lt-pv__table-panel lt-pv__table-panel--gamified lt-pv__station-panel">
      <StationRecordingRoster tables={stationTables} t={t} />
    </div>
  ) : null;

  const isOffTable =
    isOffTableProp != null
      ? Boolean(isOffTableProp)
      : isOffTableExercise(exercise);
  const coachTools = coachToolsProp || coachToolsFromExercise(exercise);
  const showOffTableChrono = isOffTable && Boolean(coachTools?.chrono);
  const showOffTableCount = isOffTable && Boolean(coachTools?.count);

  const chromeHeader = (
    <PlayerChromeHeader
      user={user}
      session={session}
      detail={chromeDetail}
      onDashboard={onDashboard}
      onRefresh={onRefresh}
      refreshing={refreshing}
      isLandscape={isLandscape}
      isFrontCamera={isFrontCamera}
      t={t}
    />
  );

  const portraitHrBlock = (
    <PortraitViewerHrBlock
      panelDisplayMode={panelDisplayMode}
      showHrToggle={showHrToggle}
      showHrUi={showHrUi}
      onTogglePanelMode={handleTogglePanelMode}
      viewerHrSamples={viewerHrSamples}
      mhr={mhr}
      viewerHr={viewerHr}
      viewerMaxHr={viewerMaxHr}
      viewerIsSparring={viewerIsSparring}
      liveZone={liveZone}
      zoneFill={zoneFill}
      zoneFg={zoneFg}
      playerName={playerName}
      classement={viewerClassementValue}
      rankingPoints={viewerRankingValue}
      roleText={roleText}
      t={t}
    />
  );

  /* HR chip — only when strap data is actively captured */
  const showHrZonePill =
    showHrUi && !isWaiting && viewerHr != null && viewerHr > 0 && !viewerIsSparring;
  const hrChip = showHrUi ? (
    <div
      className={`lt-pv__hr-chip${showHrZonePill ? " lt-pv__hr-chip--zone" : ""}`}
      data-tour-id="live-player-hr"
      style={
        showHrZonePill
          ? {
              "--lt-zone": zoneFill,
              "--lt-zone-fg": zoneFg,
            }
          : undefined
      }
    >
      <FavoriteIcon className="lt-pv__hr-icon" />
      <span>{viewerHr != null ? viewerHr : "--"}</span>
    </div>
  ) : null;

  /* ── OFF-TABLE LANDSCAPE: exercise + roster + chrono/count ─────────────── */
  if (isOffTable && isLandscape) {
    const exerciseStateLabel = isWaiting
      ? t("waiting", "Waiting")
      : isPaused
        ? t("paused", "Paused")
        : t("live", "Live");
    return (
      <LiveTrainingOffTableLandscape
        chromeHeader={chromeHeader}
        titleText={titleText}
        typeText={typeText}
        subTypeText={subTypeText}
        exerciseStateLabel={exerciseStateLabel}
        isPaused={isPaused}
        isWaiting={isWaiting}
        showChrono={showOffTableChrono || !showOffTableCount}
        showCount={showOffTableCount}
        countLabel={coachTools?.count_label || ""}
        socket={socket}
        teId={exercise?.te_id || null}
        tsgId={tsgId}
        viewerUserId={user?.user_id ?? myTablePlayer?.user_id}
        viewerPlayerId={viewerPlayerId}
        viewerName={playerName}
      />
    );
  }

  if (stationPhoneOn) {
    return (
      <div
        className={`lt-pv lt-pv--station${
          isLandscape ? " lt-pv--station-landscape" : ""
        }${isPaused ? " lt-pv--paused" : ""}`}
      >
        {chromeHeader}
        {!isWaiting ? (
          <div className="lt-pv__station-clocks">
            {showSeqTimer ? (
              <span>
                {t("liveTrainingSequence", "Sequence")} {seqNumber || 1}{" "}
                {formatTimer(
                  awaitingAutoSequenceStart
                    ? sequenceTransitionPauseRemaining ?? 0
                    : seqDisplay
                )}
              </span>
            ) : null}
            {exDisplay != null ? (
              <span>
                {t("liveTrainingDrillTotal", "Total drill")}{" "}
                {formatTimer(exDisplay)}
              </span>
            ) : null}
          </div>
        ) : null}
        {stationRoster}
      </div>
    );
  }

  /* ── WAITING STATE ──────────────────────────────────────────────────────── */
  if (isWaiting) {
    /* Landscape: iOS-style 3-panel table view */
    if (isLandscape) {
      // Determine viewer side from sequenceUpdate data or from this table's sequence
      const waitSeq = tableSeq || pickViewerSequence(exercise);
      const playerAId = waitSeq?.player_a_id ?? null;
      const playerBId = waitSeq?.player_b_id ?? null;

      let landscapeSide = resolvedSide;
      if (!landscapeSide && viewerPlayerId) {
        if (playerAId && String(playerAId) === String(viewerPlayerId))
          landscapeSide = "A";
        else if (playerBId && String(playerBId) === String(viewerPlayerId))
          landscapeSide = "B";
      }
      const viewerIsA = !landscapeSide || landscapeSide === "A";

      const waitPlayerA =
        landscapePlayerA ||
        resolveLandscapeSidePlayer("A", tablePlayers, waitSeq, roster, viewerPlayerId);
      const waitPlayerB =
        landscapePlayerB ||
        resolveLandscapeSidePlayer(
          "B",
          tablePlayers,
          waitSeq,
          roster,
          viewerPlayerId,
          waitPlayerA?.playerId ? [waitPlayerA.playerId] : landscapePlayerA?.playerId
            ? [landscapePlayerA.playerId]
            : []
        );
      const roleA = normalizeExerciseRole(waitPlayerA?.role ?? waitSeq?.player_a_role, t, roleOpts);
      const roleB = normalizeExerciseRole(waitPlayerB?.role ?? waitSeq?.player_b_role, t, roleOpts);

      return (
        <div className="lt-pv lt-pv--waiting lt-pv--waiting-landscape">
          {chromeHeader}

          {/* 3-panel split */}
          <div className="lt-pv__table-split">
            <LandscapePlayerSide
              side="A"
              player={waitPlayerA}
              role={roleA}
              roleKey={waitPlayerA?.role || waitSeq?.player_a_role || "active"}
              isViewer={viewerIsA}
              viewerHr={viewerHr}
              {...landscapeSideProps}
            />

            {/* Center: club logo + table + waiting message */}
            <div className="lt-pv__table-center">
              <TableCenterBrand user={user} tableNumber={tableNumber} t={t} />
              <p className="lt-pv__wait-message-ls">
                {t("liveTrainingWaitingCoachStart")}
              </p>
            </div>

            <LandscapePlayerSide
              side="B"
              player={waitPlayerB}
              role={roleB}
              roleKey={waitPlayerB?.role || waitSeq?.player_b_role || "sparring"}
              isViewer={!viewerIsA}
              viewerHr={viewerHr}
              {...landscapeSideProps}
            />
          </div>
        </div>
      );
    }

    /* Portrait: gamified standby cockpit */
    return (
      <div className="lt-pv lt-pv--waiting lt-pv--portrait">
        {chromeHeader}
        <div className="lt-pv__wait-cockpit">
          <div className="lt-pv__standby-radar">
            <span className="lt-pv__radar-wave lt-pv__radar-wave--1" />
            <span className="lt-pv__radar-wave lt-pv__radar-wave--2" />
            <span className="lt-pv__radar-dot" />
            <span className="lt-pv__radar-text">
              {t("liveTrainingWaitingCoachStart", "READY // STANDBY FOR COACH START")}
            </span>
          </div>

          {portraitTablePanel}

          {portraitHrBlock}
        </div>
      </div>
    );
  }

  /* ── ACTIVE STATE ───────────────────────────────────────────────────────── */

  /* KoT active: chrome + optional live HR chip */
  if (isKot) {
    return (
      <div className="lt-pv lt-pv--kot-header">
        {chromeHeader}
        {hrChip ? (
          <div className="lt-pv__kot-hr-row" data-tour-id="live-player-hr">
            {hrChip}
          </div>
        ) : null}
      </div>
    );
  }

  /* landscape active: 3-panel table view (A | timers | B) with classement + ranking */
  if (isLandscape) {
    const roleA = normalizeExerciseRole(
      landscapePlayerA?.role ?? tableSeq?.player_a_role ?? activeSeq?.player_a_role,
      t,
      roleOpts
    );
    const roleB = normalizeExerciseRole(
      landscapePlayerB?.role ?? tableSeq?.player_b_role ?? activeSeq?.player_b_role,
      t,
      roleOpts
    );
    const viewerIsA =
      landscapePlayerA?.isViewer ||
      (!landscapePlayerB?.isViewer &&
        resolvedSide === "A") ||
      (!landscapePlayerA?.isViewer &&
        !landscapePlayerB?.isViewer &&
        (!resolvedSide || resolvedSide === "A"));

    return (
      <div
        className={`lt-pv lt-pv--active lt-pv--active-landscape${
          isPaused ? " lt-pv--paused" : ""
        }`}
      >
        {chromeHeader}

        <div className="lt-pv__table-split">
          <LandscapePlayerSide
            side="A"
            player={landscapePlayerA}
            role={roleA}
            roleKey={landscapePlayerA?.role || tableSeq?.player_a_role || activeSeq?.player_a_role || "active"}
            isViewer={viewerIsA}
            viewerHr={viewerHr}
            {...landscapeSideProps}
          />

          <div className="lt-pv__table-center lt-pv__table-center--active">
            <div className="lt-pv__table-center-pin">
              <TableCenterBrand user={user} tableNumber={tableNumber} t={t} />
            </div>

            {awaitingAutoSequenceStart ? (
              <div className="lt-pv__ls-timer lt-pv__ls-timer--seq lt-pv__ls-timer--transition">
                <span className="lt-pv__timer-label">
                  {t("sequenceTransitionPause") || "Next sequence in"}
                </span>
                <span className="lt-pv__timer-value lt-pv__timer-value--seq lt-pv__timer-value--ls-seq lt-pv__timer-value--transition">
                  {formatTimer(sequenceTransitionPauseRemaining ?? 0)}
                </span>
              </div>
            ) : showSeqTimer ? (
              <div className="lt-pv__ls-timer lt-pv__ls-timer--seq">
                <span className="lt-pv__timer-label">
                  {t("liveTrainingSequence")} {seqNumber}
                </span>
                <span className="lt-pv__timer-value lt-pv__timer-value--seq lt-pv__timer-value--ls-seq">
                  {formatTimer(seqDisplay)}
                </span>
              </div>
            ) : null}

            <div className="lt-pv__ls-timer lt-pv__ls-timer--ex">
              <span className="lt-pv__timer-label">{t("exercise")}</span>
              <span className="lt-pv__timer-value lt-pv__timer-value--ex lt-pv__timer-value--ls-ex">
                {formatTimer(exDisplay)}
              </span>
            </div>

            {isPaused ? (
              <span className="lt-pv__paused-tag lt-pv__paused-tag--center">
                {t("paused")}
              </span>
            ) : null}
          </div>

          <LandscapePlayerSide
            side="B"
            player={landscapePlayerB}
            role={roleB}
            roleKey={landscapePlayerB?.role || tableSeq?.player_b_role || activeSeq?.player_b_role || "sparring"}
            isViewer={!viewerIsA}
            viewerHr={viewerHr}
            {...landscapeSideProps}
          />
        </div>
      </div>
    );
  }

  const totalSequences = Math.max(
    1,
    (exercise?.sequences && exercise.sequences.length) || 1
  );
  const currentSeqNum = seqNumber || 1;

  /* portrait: stacked single column with modern gamification sports HUD */
  return (
    <div
      className={`lt-pv lt-pv--active lt-pv--portrait${
        isPaused ? " lt-pv--paused" : ""
      }`}
    >
      <div className="lt-pv__portrait-top">
        {chromeHeader}
        {portraitTablePanel}
      </div>

      {/* Sequence Segmented Progression Meter */}
      {showSeqTimer ? (
        <div className="lt-pv__seq-progress-wrap">
          <div className="lt-pv__seq-meter">
            <div className="lt-pv__seq-pips">
              {Array.from({ length: totalSequences }, (_, i) => {
                const stepNum = i + 1;
                const isPast = stepNum < currentSeqNum;
                const isCurrent = stepNum === currentSeqNum;
                return (
                  <span
                    key={stepNum}
                    className={`lt-pv__seq-pip${
                      isPast
                        ? " lt-pv__seq-pip--done"
                        : isCurrent
                        ? " lt-pv__seq-pip--active"
                        : ""
                    }`}
                    title={`Sequence ${stepNum} of ${totalSequences}`}
                  />
                );
              })}
            </div>
            <div className="lt-pv__seq-label-row">
              <span className="lt-pv__seq-counter-tag">
                {t("liveTrainingSequenceProgress", {
                  current: currentSeqNum,
                  total: totalSequences,
                  defaultValue: `Sequence ${currentSeqNum} / ${totalSequences}`,
                })}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {/* Dominant Split-Countdown Timer */}
      {awaitingAutoSequenceStart ? (
        <div className="lt-pv__timer-card lt-pv__timer-card--rest">
          <div className="lt-pv__timer-card-head">
            <WarningAmberIcon className="lt-pv__timer-rest-icon" />
            <span className="lt-pv__timer-card-title">
              {t("liveTrainingRestInterval", "REST INTERVAL // NEXT SEQUENCE")}
            </span>
          </div>
          <div className="lt-pv__timer-value lt-pv__timer-value--rest">
            {formatTimer(sequenceTransitionPauseRemaining ?? 0)}
          </div>
          <span className="lt-pv__timer-rest-sub">
            {t("liveTrainingRestHint", "Rotate sides, hydrate & prepare")}
          </span>
        </div>
      ) : showSeqTimer ? (
        <div className="lt-pv__timer-card lt-pv__timer-card--seq">
          <div className="lt-pv__timer-card-head">
            <TimerIcon className="lt-pv__timer-head-icon" />
            <span className="lt-pv__timer-card-title">
              {t("liveTrainingSequence", "Sequence")} {seqNumber}
            </span>
            {isPaused ? (
              <span className="lt-pv__timer-paused-badge">
                <PauseIcon fontSize="inherit" />
                {t("paused", "PAUSED")}
              </span>
            ) : null}
          </div>
          <div className="lt-pv__timer-value lt-pv__timer-value--seq">
            {formatTimer(seqDisplay)}
          </div>
        </div>
      ) : null}

      {/* Drill Total Stopwatch Odometer */}
      <div className="lt-pv__drill-odometer">
        <span className="lt-pv__odometer-label">
          {t("liveTrainingDrillTotal", "TOTAL DRILL")}
        </span>
        <span className="lt-pv__odometer-value">{formatTimer(exDisplay)}</span>
      </div>

      {/* compact HR row or 60s trend chart */}
      {portraitHrBlock}

      {isPaused ? (
        <span className="lt-pv__paused-tag">{t("paused")}</span>
      ) : null}
    </div>
  );
}

export default LiveTrainingPlayerView;
